/**
 * Background Sync Manager
 *
 * Handles synchronization of queued offline actions for the Democratic Services PWA.
 * Works with the Service Worker and Offline Queue to sync data when connectivity
 * is restored.
 *
 * @package DemocraticServices
 * @since 3.1.0
 * @author Democratic Services Team
 *
 * Design Decisions:
 * - Syncs actions in FIFO order to maintain chronological integrity
 * - Implements exponential backoff for failed retries
 * - Supports both manual and automatic (background) sync
 * - Gracefully degrades when Background Sync API is unavailable
 */

import {
	getPendingActions,
	updateActionStatus,
	incrementRetryCount,
	logSyncAttempt,
	ACTION_STATUS,
	ACTION_TYPES,
	queueAction,
} from './offline-queue.js';

// =============================================================================
// CONFIGURATION
// =============================================================================

/**
 * Nonce refresh URL (canonical democratic/v1), respecting subdirectory installs via wp_localize_script restUrl.
 *
 * @return {string}
 */
function getPwaNonceRefreshEndpoint() {
	if (
		typeof democraticServices !== 'undefined' &&
		democraticServices.restUrl
	) {
		return `${String(democraticServices.restUrl).replace(/\/?$/, '/')}democratic/v1/pwa/nonce`;
	}
	return '/wp-json/democratic/v1/pwa/nonce';
}

const SYNC_CONFIG = {
	// Sync tags that match service worker registration
	tags: {
		petitions: 'sync-petition-signature',
		consultations: 'sync-consultation-response',
	},

	// Base delay for exponential backoff (in milliseconds)
	baseRetryDelay: 1000,

	// Maximum delay between retries
	maxRetryDelay: 30000,

	// Timeout for individual sync requests
	requestTimeout: 30000,

	// WordPress admin-ajax.php path
	ajaxUrl: '/wp-admin/admin-ajax.php',

	get nonceRefreshEndpoint() {
		return getPwaNonceRefreshEndpoint();
	},
};

// =============================================================================
// NONCE CACHE
// =============================================================================

/**
 * Cache for fresh nonces, keyed by action type
 * @type {Object<string, string>}
 */
let cachedNonces = {};

// =============================================================================
// SYNC STATE
// =============================================================================

/**
 * Track sync state to prevent concurrent syncs
 */
let isSyncing = false;

/**
 * Online status
 */
let isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

/**
 * Track if network monitoring is already initialized
 */
let networkMonitoringInitialized = false;

/**
 * Track if background sync is already initialized
 */
let backgroundSyncInitialized = false;

// =============================================================================
// NETWORK STATUS MONITORING
// =============================================================================

/**
 * Initialize network status monitoring
 *
 * Listens for online/offline events and triggers sync when back online.
 */
export function initNetworkMonitoring() {
	if (typeof window === 'undefined') return;

	// Prevent duplicate initialization
	if (networkMonitoringInitialized) {
		return;
	}
	networkMonitoringInitialized = true;

	window.addEventListener('online', handleOnline);
	window.addEventListener('offline', handleOffline);

	// Initial status check
	isOnline = navigator.onLine;

	// Network monitoring initialized
}

/**
 * Handle coming back online
 */
async function handleOnline() {
	isOnline = true;

	// Dispatch event for UI updates
	dispatchSyncEvent('online');

	// Attempt to sync pending actions
	await syncAllPending();
}

/**
 * Handle going offline
 */
function handleOffline() {
	isOnline = false;

	// Dispatch event for UI updates
	dispatchSyncEvent('offline');
}

/**
 * Check if currently online
 *
 * @returns {boolean} True if online
 */
export function getOnlineStatus() {
	return isOnline;
}

// =============================================================================
// SYNC OPERATIONS
// =============================================================================

/**
 * Sync all pending actions
 *
 * Processes all queued actions in order. This is the main entry point
 * for syncing, called either manually or via background sync.
 *
 * @returns {Promise<Object>} Sync results summary
 */
export async function syncAllPending() {
	// Prevent concurrent syncs
	if (isSyncing) {
		return { skipped: true };
	}

	// Check network status
	if (!isOnline) {
		return { skipped: true, reason: 'offline' };
	}

	isSyncing = true;
	dispatchSyncEvent('syncStarted');

	const results = {
		total: 0,
		success: 0,
		failed: 0,
		pending: 0,
	};

	try {
		// Fetch fresh nonces before syncing to avoid expired nonce errors
		await refreshNonces();

		// Get all pending actions, ordered by timestamp
		const pendingActions = await getPendingActions();
		results.total = pendingActions.length;

		if (pendingActions.length === 0) {
			return results;
		}

		// Process actions sequentially to maintain order
		for (const action of pendingActions) {
			try {
				const success = await syncAction(action);

				if (success) {
					results.success++;
				} else {
					results.failed++;
				}
			} catch (error) {
				// Error syncing individual action (silent)
				results.failed++;
			}
		}

		results.pending = results.total - results.success - results.failed;

		// Sync complete
		dispatchSyncEvent('syncCompleted', results);

		return results;
	} catch (error) {
		dispatchSyncEvent('syncError', { error: error.message });
		throw error;
	} finally {
		isSyncing = false;
	}
}

/**
 * Nonce lifetime threshold in milliseconds (12 hours)
 * Actions older than this should have their nonce refreshed before sync
 */
const NONCE_LIFETIME_MS = 12 * 60 * 60 * 1000;

/**
 * Sync a single action
 *
 * Submits a queued action to the server and updates its status.
 * Automatically refreshes nonces for stale actions to prevent security errors.
 *
 * @param {Object} action - The action to sync
 * @returns {Promise<boolean>} True if sync was successful
 */
async function syncAction(action) {
	const { id, type, url, method, data } = action;

	// Syncing action (silent)

	// Mark as syncing
	await updateActionStatus(id, ACTION_STATUS.SYNCING);

	try {
		// Check if action is stale and needs nonce refresh
		const actionAge = Date.now() - (action.timestamp || 0);
		if (actionAge > NONCE_LIFETIME_MS) {
			// Action is older than nonce lifetime - refresh nonces before syncing
			await refreshNonces();
		}

		// Determine the correct endpoint based on action type
		const endpoint = getEndpointForAction(type, url);

		// Prepare form data
		const formData = new FormData();

		// Add action type for WordPress AJAX
		formData.append('action', getAjaxAction(type));

		// Add submission ID for server-side deduplication
		if (action.submissionId || (data && data._submission_id)) {
			formData.append(
				'_submission_id',
				action.submissionId || data._submission_id,
			);
		}

		// Add all stored data, but replace stale nonce with fresh one if available
		const nonceFieldName = getNonceFieldName(type);
		const freshNonce = getFreshNonce(type);

		for (const [key, value] of Object.entries(data)) {
			if (value !== null && value !== undefined) {
				// Replace expired nonce with fresh one
				if (key === nonceFieldName && freshNonce) {
					// Replacing stale nonce with fresh nonce for this action type
					formData.append(key, freshNonce);
				} else {
					formData.append(key, value);
				}
			}
		}

		// If nonce field wasn't in the data but we have a fresh one, add it
		if (freshNonce && !data[nonceFieldName]) {
			formData.append(nonceFieldName, freshNonce);
		}

		// Add flag to indicate this was synced from offline queue
		formData.append('_offline_sync', '1');
		formData.append('_sync_timestamp', action.timestamp.toString());

		// Create abort controller for timeout
		const controller = new AbortController();
		const timeoutId = setTimeout(
			() => controller.abort(),
			SYNC_CONFIG.requestTimeout,
		);

		// Make the request
		const response = await fetch(endpoint, {
			method: method || 'POST',
			body: formData,
			credentials: 'same-origin',
			signal: controller.signal,
		});

		clearTimeout(timeoutId);

		// Parse response body first (even for non-2xx, WordPress returns JSON error details)
		let responseData;
		try {
			responseData = await response.json();
		} catch {
			responseData = { success: response.ok };
		}

		// Handle client errors (4xx) — validation/input errors that will never succeed on retry
		if (!response.ok && response.status >= 400 && response.status < 500) {
			const validationMessage =
        (responseData.data && responseData.data.message) ||
        responseData.message ||
        `Request rejected (${response.status})`;
			const validationError = new Error(validationMessage);
			validationError.isValidationError = true;
			throw validationError;
		}

		// Handle server errors (5xx) — transient, worth retrying
		if (!response.ok) {
			throw new Error(`HTTP ${response.status}: ${response.statusText}`);
		}

		// Check for success in response body
		if (responseData.success === false) {
			const serverMessage =
        (responseData.data && responseData.data.message) ||
        responseData.message ||
        'Server returned error';
			throw new Error(serverMessage);
		}

		// Success!
		await updateActionStatus(id, ACTION_STATUS.SUCCESS);
		await logSyncAttempt({
			actionId: id,
			type,
			success: true,
		});

		// Record completed sync for deferred notification (page refresh scenario)
		recordCompletedSync(id, type);

		dispatchSyncEvent('actionSynced', { id, type, success: true });

		return true;
	} catch (error) {
		// Action sync failed (logged silently)

		// Log the failure
		await logSyncAttempt({
			actionId: id,
			type,
			success: false,
			error: error.message,
		});

		// Validation errors (4xx) — fail immediately, no retries
		if (error.isValidationError) {
			await updateActionStatus(id, ACTION_STATUS.FAILED, {
				lastError: error.message,
			});
			dispatchSyncEvent('actionFailed', {
				id,
				type,
				error: error.message,
				isValidationError: true,
			});
			dispatchSyncEvent('actionSynced', { id, type, success: false });
			return false;
		}

		// Check if we should retry (transient errors only)
		const canRetry = await incrementRetryCount(id, error.message);

		if (!canRetry) {
			dispatchSyncEvent('actionFailed', { id, type, error: error.message });
		} else {
			// Schedule retry with exponential backoff
			const retryDelay = calculateRetryDelay(action.retryCount);
			const retryNumber = (action.retryCount || 0) + 1;

			// Dispatch event so UI can show retry indicator
			dispatchSyncEvent('actionRetrying', {
				id,
				type,
				retryNumber,
				retryDelay: Math.round(retryDelay / 1000),
				error: error.message,
			});

			setTimeout(() => {
				if (isOnline) {
					syncAction({ ...action, retryCount: retryNumber });
				}
			}, retryDelay);
		}

		dispatchSyncEvent('actionSynced', { id, type, success: false });

		return false;
	}
}

/**
 * Get the correct endpoint for an action type
 *
 * @param {string} type - Action type
 * @param {string} originalUrl - Original request URL
 * @returns {string} The endpoint URL
 */
function getEndpointForAction(type, originalUrl) {
	// Use original URL if it's an admin-ajax.php URL
	if (originalUrl && originalUrl.includes('admin-ajax.php')) {
		return originalUrl;
	}

	// Default to WordPress admin-ajax.php
	return SYNC_CONFIG.ajaxUrl;
}

/**
 * Get the WordPress AJAX action name for an action type
 *
 * @param {string} type - Action type
 * @returns {string} WordPress AJAX action name
 */
function getAjaxAction(type) {
	switch (type) {
		case ACTION_TYPES.PETITION_SIGNATURE:
			return 'sign_petition';
		case ACTION_TYPES.PETITION_CREATE:
			return 'create_petition';
		case ACTION_TYPES.CONSULTATION_RESPONSE:
			return 'submit_consultation_response';
		case ACTION_TYPES.NEWSLETTER_SIGNUP:
			return 'bcp_newsletter_signup';
		case ACTION_TYPES.VOTE_CAST:
			return 'democratic_cast_vote';
		case ACTION_TYPES.CALL_IN_DECISION:
			return 'democratic_call_in_decision';
		default:
			return 'democratic_ajax_generic';
	}
}

/**
 * Fetch fresh nonces from the server
 *
 * Nonces stored with queued actions may expire before sync occurs.
 * This function fetches fresh nonces from the REST API endpoint.
 *
 * @returns {Promise<Object>} Object containing fresh nonces keyed by action type
 */
async function refreshNonces() {
	try {
		const response = await fetch(SYNC_CONFIG.nonceRefreshEndpoint, {
			method: 'GET',
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/json',
			},
		});

		if (!response.ok) {
			return null;
		}

		const data = await response.json();

		if (data.success && data.nonces) {
			cachedNonces = data.nonces;
			return cachedNonces;
		}

		return null;
	} catch (error) {
		return null;
	}
}

/**
 * Get the nonce field name for an action type
 *
 * @param {string} type - Action type
 * @returns {string} The nonce field name used in forms
 */
function getNonceFieldName(type) {
	switch (type) {
		case ACTION_TYPES.PETITION_SIGNATURE:
			return 'petition_nonce';
		case ACTION_TYPES.PETITION_CREATE:
			return 'petition_nonce';
		case ACTION_TYPES.CONSULTATION_RESPONSE:
			return 'consultation_nonce';
		case ACTION_TYPES.VOTE_CAST:
			return 'nonce'; // Vote form uses "nonce" field name
		case ACTION_TYPES.CALL_IN_DECISION:
			return 'call_in_nonce';
		default:
			return '_wpnonce';
	}
}

/**
 * Get a fresh nonce for an action type
 *
 * @param {string} type - Action type
 * @returns {string|null} Fresh nonce or null if unavailable
 */
function getFreshNonce(type) {
	// Map action type to nonce key in cached nonces (from /pwa/nonce endpoint)
	const nonceKeyMap = {
		[ACTION_TYPES.PETITION_SIGNATURE]: 'petition_signature',
		[ACTION_TYPES.PETITION_CREATE]: 'petition_create',
		[ACTION_TYPES.CONSULTATION_RESPONSE]: 'consultation_response',
		[ACTION_TYPES.VOTE_CAST]: 'vote_cast',
		[ACTION_TYPES.NEWSLETTER_SIGNUP]: 'newsletter_signup',
		[ACTION_TYPES.CALL_IN_DECISION]: 'call_in_decision',
	};
	const nonceKey = nonceKeyMap[type] || 'generic';
	return cachedNonces[nonceKey] || cachedNonces.generic || null;
}

/**
 * Calculate retry delay with exponential backoff
 *
 * @param {number} retryCount - Current retry count
 * @returns {number} Delay in milliseconds
 */
function calculateRetryDelay(retryCount) {
	// Exponential backoff: base * 2^retryCount
	const delay = SYNC_CONFIG.baseRetryDelay * Math.pow(2, retryCount);

	// Add some jitter to prevent thundering herd
	const jitter = Math.random() * 1000;

	return Math.min(delay + jitter, SYNC_CONFIG.maxRetryDelay);
}

// =============================================================================
// SYNC TYPE SPECIFIC FUNCTIONS
// =============================================================================

/**
 * Sync petition signatures
 *
 * Syncs only petition-related actions. Called by service worker
 * via message passing.
 *
 * @returns {Promise<Object>} Sync results
 */
export async function syncPetitionSignatures() {
	if (!isOnline) {
		return { skipped: true };
	}

	const pendingPetitions = await getPendingActions(
		ACTION_TYPES.PETITION_SIGNATURE,
	);

	const results = {
		total: pendingPetitions.length,
		success: 0,
		failed: 0,
	};

	for (const action of pendingPetitions) {
		const success = await syncAction(action);
		if (success) {
			results.success++;
		} else {
			results.failed++;
		}
	}

	return results;
}

/**
 * Sync consultation responses
 *
 * Syncs only consultation-related actions. Called by service worker
 * via message passing.
 *
 * @returns {Promise<Object>} Sync results
 */
export async function syncConsultationResponses() {
	if (!isOnline) {
		return { skipped: true };
	}

	const pendingConsultations = await getPendingActions(
		ACTION_TYPES.CONSULTATION_RESPONSE,
	);

	const results = {
		total: pendingConsultations.length,
		success: 0,
		failed: 0,
	};

	for (const action of pendingConsultations) {
		const success = await syncAction(action);
		if (success) {
			results.success++;
		} else {
			results.failed++;
		}
	}

	return results;
}

// =============================================================================
// SERVICE WORKER COMMUNICATION
// =============================================================================

/**
 * Register for background sync
 *
 * Registers sync tags with the service worker for background sync.
 *
 * @param {string} tag - Sync tag to register
 * @returns {Promise<boolean>} True if registration was successful
 */
export async function registerBackgroundSync(tag) {
	if (!('serviceWorker' in navigator)) {
		return false;
	}

	try {
		const registration = await navigator.serviceWorker.ready;

		if ('sync' in registration) {
			await registration.sync.register(tag);
			return true;
		} else {
			return false;
		}
	} catch (error) {
		// Failed to register sync (silent)
		return false;
	}
}

/**
 * Handle messages from service worker
 *
 * Processes sync requests from the service worker.
 */
export function initServiceWorkerMessageHandler() {
	if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
		return;
	}

	navigator.serviceWorker.addEventListener('message', async (event) => {
		const { type, payload } = event.data || {};

		switch (type) {
			case 'SYNC_PETITION_SIGNATURES':
				await syncPetitionSignatures();
				break;

			case 'SYNC_CONSULTATION_RESPONSES':
				await syncConsultationResponses();
				break;

			case 'QUEUE_OFFLINE_ACTION':
				// Payload expected to include: { url, method, data, timestamp, syncTag }
				try {
					// Determine action type from syncTag, url or data.action
					const syncTag = (payload && payload.syncTag) || '';
					const data = (payload && payload.data) || {};
					const url = (payload && payload.url) || SYNC_CONFIG.ajaxUrl;

					let actionType = ACTION_TYPES.PETITION_SIGNATURE;

					if (
						syncTag.includes('petition') ||
            url.includes('sign_petition') ||
            (data.action && data.action.includes('petition'))
					) {
						// Distinguish create vs sign if possible
						if (data.action && data.action.includes('create')) {
							actionType = ACTION_TYPES.PETITION_CREATE;
						} else {
							actionType = ACTION_TYPES.PETITION_SIGNATURE;
						}
					} else if (
						syncTag.includes('consultation') ||
            url.includes('submit_consultation') ||
            (data.action && data.action.includes('consultation'))
					) {
						actionType = ACTION_TYPES.CONSULTATION_RESPONSE;
					}

					// Queue the action into IndexedDB so background sync can process it
					const id = await queueAction({
						type: actionType,
						url: url,
						method: payload.method || 'POST',
						data: data,
						metadata: {
							timestamp: payload.timestamp || Date.now(),
							syncTag: payload.syncTag,
						},
					});

					// Notify UI about queued action (include id and action)
					dispatchSyncEvent('actionQueued', {
						id,
						action: { id, type: actionType },
					});
				} catch (err) {
					// Ignore failures to queue from SW
				}

				break;

			default:
				break;
		}
	});
}

// =============================================================================
// PERIODIC SYNC (FALLBACK)
// =============================================================================

/**
 * Start periodic sync as fallback for browsers without Background Sync
 *
 * @param {number} intervalMs - Sync interval in milliseconds (default: 30s)
 */
let periodicSyncInterval = null;

export function startPeriodicSync(intervalMs = 30000) {
	if (periodicSyncInterval) {
		return; // Already running
	}

	periodicSyncInterval = setInterval(async () => {
		if (isOnline && !isSyncing) {
			await syncAllPending();
		}
	}, intervalMs);
	// Periodic sync started
}

/**
 * Stop periodic sync
 */
export function stopPeriodicSync() {
	if (periodicSyncInterval) {
		clearInterval(periodicSyncInterval);
		periodicSyncInterval = null;
	}
}

// =============================================================================
// EVENT DISPATCHING
// =============================================================================

/**
 * Dispatch a custom sync event
 *
 * @param {string} eventName - Name of the event
 * @param {Object} detail - Event detail data
 */
function dispatchSyncEvent(eventName, detail = {}) {
	if (typeof window !== 'undefined') {
		const event = new CustomEvent(`backgroundSync:${eventName}`, { detail });
		window.dispatchEvent(event);
	}
}

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Initialize background sync functionality
 */
export function initBackgroundSync() {
	// Prevent duplicate initialization
	if (backgroundSyncInitialized) {
		return;
	}
	backgroundSyncInitialized = true;

	// Initialize network monitoring
	initNetworkMonitoring();

	// Initialize service worker message handler
	initServiceWorkerMessageHandler();

	// Initialize localStorage sync status persistence
	initSyncStatusPersistence();

	// Check for Background Sync support
	if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
		navigator.serviceWorker.ready.then((registration) => {
			if (!('sync' in registration)) {
				startPeriodicSync();
			}
		});
	} else {
		// Fallback to periodic sync
		startPeriodicSync();
	}

	// Background sync initialized (silent)
}

// =============================================================================
// SYNC STATUS PERSISTENCE (localStorage)
// =============================================================================

const SYNC_STATUS_KEY = 'democratic_sync_status';
const SYNC_COMPLETED_KEY = 'democratic_sync_completed';

/**
 * Initialize sync status persistence for page refresh scenarios
 */
function initSyncStatusPersistence() {
	if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
		return;
	}

	// Check for completed syncs from previous session
	checkDeferredSyncNotifications();

	// Store pending sync IDs before page unload
	window.addEventListener('beforeunload', savePendingSyncStatus);
}

/**
 * Save pending sync IDs to localStorage before page unload
 */
async function savePendingSyncStatus() {
	try {
		const pending = await getPendingActions();
		if (pending.length > 0) {
			const pendingIds = pending.map((a) => ({
				id: a.id,
				type: a.type,
				timestamp: Date.now(),
			}));
			localStorage.setItem(SYNC_STATUS_KEY, JSON.stringify(pendingIds));
		}
	} catch (e) {
		// Ignore errors during page unload
	}
}

/**
 * Record a completed sync for deferred notification
 *
 * @param {string} id - Action ID
 * @param {string} type - Action type
 */
export function recordCompletedSync(id, type) {
	if (typeof localStorage === 'undefined') return;

	try {
		const completed = JSON.parse(
			localStorage.getItem(SYNC_COMPLETED_KEY) || '[]',
		);
		completed.push({
			id,
			type,
			timestamp: Date.now(),
		});
		// Keep only last 10 completed items and items from last hour
		const hourAgo = Date.now() - 60 * 60 * 1000;
		const filtered = completed.filter((c) => c.timestamp > hourAgo).slice(-10);
		localStorage.setItem(SYNC_COMPLETED_KEY, JSON.stringify(filtered));
	} catch (e) {
		// Ignore localStorage errors
	}
}

/**
 * Check for completed syncs after page reload and show deferred notifications
 */
function checkDeferredSyncNotifications() {
	try {
		const savedPending = JSON.parse(
			localStorage.getItem(SYNC_STATUS_KEY) || '[]',
		);
		const completed = JSON.parse(
			localStorage.getItem(SYNC_COMPLETED_KEY) || '[]',
		);

		if (savedPending.length === 0 || completed.length === 0) {
			return;
		}

		// Find items that were pending before but are now completed
		const fiveMinAgo = Date.now() - 5 * 60 * 1000;
		const recentlyCompleted = completed.filter((c) => c.timestamp > fiveMinAgo);

		const pendingIds = new Set(savedPending.map((p) => p.id));
		const syncedWhileAway = recentlyCompleted.filter((c) =>
			pendingIds.has(c.id),
		);

		if (syncedWhileAway.length > 0) {
			// Show deferred notification
			setTimeout(() => {
				dispatchSyncEvent('deferredSyncCompleted', {
					count: syncedWhileAway.length,
					items: syncedWhileAway,
				});
			}, 1000); // Delay to ensure UI is ready
		}

		// Clear saved pending status
		localStorage.removeItem(SYNC_STATUS_KEY);
	} catch (e) {
		// Ignore errors
	}
}

// Auto-initialize if in browser environment
if (typeof window !== 'undefined') {
	// Initialize on DOMContentLoaded to ensure all dependencies are loaded
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initBackgroundSync);
	} else {
		initBackgroundSync();
	}
}
